import { useState, useRef, useEffect } from "react";
import { CalendarPlus, Check } from "lucide-react";

/**
 * "Ajouter à mon calendrier" — offers Google, Outlook (Live/O365) and .ics download
 * so the user isn't forced into Apple Calendar.
 */
export default function CalendarAddButton({
  title,
  description = "",
  location = "",
  startISO,
  durationMin = 30,
  icsHref, // authenticated fetch source (from backend)
  filenameHint = "rdv",
  testIdPrefix = "cal",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const start = startISO ? new Date(startISO) : null;
  const end = start ? new Date(start.getTime() + durationMin * 60000) : null;
  const fmt = (d) => d.toISOString().replace(/[-:]|\.\d{3}/g, "");

  const encoded = {
    title: encodeURIComponent(title || ""),
    desc: encodeURIComponent(description || ""),
    loc: encodeURIComponent(location || ""),
    start: start ? fmt(start) : "",
    end: end ? fmt(end) : "",
  };

  const googleUrl =
    start &&
    `https://www.google.com/calendar/render?action=TEMPLATE` +
      `&text=${encoded.title}` +
      `&dates=${encoded.start}/${encoded.end}` +
      `&details=${encoded.desc}` +
      `&location=${encoded.loc}`;

  const outlookUrl =
    start &&
    `https://outlook.live.com/calendar/0/deeplink/compose` +
      `?path=/calendar/action/compose&rru=addevent` +
      `&subject=${encoded.title}` +
      `&startdt=${start.toISOString()}` +
      `&enddt=${end.toISOString()}` +
      `&body=${encoded.desc}` +
      `&location=${encoded.loc}`;

  const office365Url =
    start &&
    `https://outlook.office.com/calendar/0/deeplink/compose` +
      `?path=/calendar/action/compose&rru=addevent` +
      `&subject=${encoded.title}` +
      `&startdt=${start.toISOString()}` +
      `&enddt=${end.toISOString()}` +
      `&body=${encoded.desc}` +
      `&location=${encoded.loc}`;

  const downloadIcs = async () => {
    if (!icsHref) return;
    try {
      const token = localStorage.getItem("ma_token");
      const res = await fetch(icsHref, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filenameHint}.ics`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } finally {
      setOpen(false);
    }
  };

  const Item = ({ onClick, href, label, hint, testId }) => (
    <a
      href={href || "#"}
      target={href ? "_blank" : undefined}
      rel="noreferrer"
      onClick={(e) => {
        if (!href) e.preventDefault();
        onClick?.();
        if (href) setOpen(false);
      }}
      className="flex items-start gap-3 px-4 py-3 hover:bg-[#DCB457]/10 transition text-left"
      data-testid={testId}
    >
      <Check size={14} strokeWidth={1.6} className="text-[#DCB457] mt-1 shrink-0" />
      <div>
        <div className="text-sm text-white font-medium">{label}</div>
        {hint && <div className="text-[11px] text-white/50 mt-0.5">{hint}</div>}
      </div>
    </a>
  );

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] uppercase tracking-[0.18em] text-[#DCB457] border border-[#DCB457]/40 px-3 py-1.5 hover:bg-[#DCB457]/10 inline-flex items-center gap-1.5"
        data-testid={`${testIdPrefix}-toggle`}
      >
        <CalendarPlus size={11} strokeWidth={1.5} /> Ajouter à mon calendrier
      </button>
      {open && (
        <div
          className="absolute z-30 right-0 mt-2 w-72 bg-[#0a0a0a] border border-[#DCB457]/25 shadow-2xl py-2"
          role="menu"
          data-testid={`${testIdPrefix}-menu`}
        >
          {googleUrl && (
            <Item
              href={googleUrl}
              label="Google Calendar"
              hint="Ouvre un nouvel événement pré-rempli"
              testId={`${testIdPrefix}-google`}
            />
          )}
          {outlookUrl && (
            <Item
              href={outlookUrl}
              label="Outlook (Live / Hotmail)"
              hint="Compte personnel Microsoft"
              testId={`${testIdPrefix}-outlook`}
            />
          )}
          {office365Url && (
            <Item
              href={office365Url}
              label="Outlook 365 (Pro)"
              hint="Compte professionnel Microsoft"
              testId={`${testIdPrefix}-o365`}
            />
          )}
          <Item
            onClick={downloadIcs}
            label="Apple Calendar / iCal (.ics)"
            hint="Téléchargement — Mac, iPhone, autres"
            testId={`${testIdPrefix}-ics`}
          />
        </div>
      )}
    </div>
  );
}
